import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { BookOpen, Upload, Rss } from 'lucide-react';

import { Layout } from '@/components/Layout';
import { ArticleCard } from '@/components/ArticleCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useArticles } from '@/hooks/useArticles';

export default function Index() {
  useSeoMeta({
    title: 'Substr — Substack to Nostr',
    description: 'Import your Substack articles and publish them as Nostr blog posts. Decentralize your writing.',
  });

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <HeroSection />
        <ArticleFeed />
      </div>
    </Layout>
  );
}

function HeroSection() {
  const { user } = useCurrentUser();

  return (
    <div className="text-center mb-12 pt-4">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/5 text-primary text-sm font-medium mb-6">
        <Rss className="size-3.5" />
        Substack to Nostr
      </div>
      <h1 className="text-4xl md:text-6xl font-bold font-serif tracking-tight mb-4 leading-tight">
        Free your writing.
        <br />
        <span className="text-primary/70">Own your words.</span>
      </h1>
      <p className="text-lg md:text-xl text-muted-foreground max-w-xl mx-auto mb-8 leading-relaxed">
        Import your Substack articles and publish them as censorship-resistant blog posts on Nostr.
      </p>
      {user && (
        <Link to="/import">
          <Button size="lg" className="rounded-full px-8">
            <Upload className="size-4 mr-2" />
            Import Article
          </Button>
        </Link>
      )}
    </div>
  );
}

function ArticleFeed() {
  const { data: articles, isLoading } = useArticles();

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <BookOpen className="size-5 text-muted-foreground" />
        <h2 className="text-xl font-semibold font-serif">Recent Articles</h2>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-5">
                <div className="flex gap-5">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-2">
                      <Skeleton className="size-6 rounded-full" />
                      <Skeleton className="h-4 w-20" />
                    </div>
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                  <Skeleton className="hidden sm:block w-32 h-24 rounded-lg" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : articles && articles.length > 0 ? (
        <div className="divide-y">
          {articles.map((event) => (
            <ArticleCard key={event.id} event={event} />
          ))}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="py-16 px-8 text-center">
            <div className="size-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <BookOpen className="size-7 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No articles yet</h3>
            <p className="text-muted-foreground max-w-sm mx-auto">
              No articles found. Be the first to import a Substack article!
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
